import React from "react";
import {
  MessageCircle,
  Users,
  Zap,
  Target,
  Lightbulb,
  TrendingUp,
  Heart,
  BookOpen,
  CheckCircle,
  Clock,
} from "lucide-react";

/** Known skill categories → chart key, labels, color, icon */
export const SCORE_CATEGORY_PRESETS = {
  overall: {
    key: "overallScore",
    label: "Overall",
    fullLabel: "Overall Score",
    color: "#3B82F6",
    icon: <TrendingUp className="w-4 h-4" />,
  },
  communication_effectiveness: {
    key: "communicationEffectiveness",
    label: "Communication",
    fullLabel: "Communication Effectiveness",
    color: "#EC4899",
    icon: <MessageCircle className="w-4 h-4" />,
  },
  student_engagement_rapport: {
    key: "studentEngagementRapport",
    label: "Engagement",
    fullLabel: "Student Engagement & Rapport",
    color: "#8B5CF6",
    icon: <Users className="w-4 h-4" />,
  },
  content_accuracy_alignment: {
    key: "contentAccuracyAlignment",
    label: "Content Accuracy",
    fullLabel: "Content Accuracy & Alignment",
    color: "#F59E0B",
    icon: <Target className="w-4 h-4" />,
  },
  instructional_clarity_structure: {
    key: "instructionalClarityStructure",
    label: "Instructional Clarity",
    fullLabel: "Instructional Clarity & Structure",
    color: "#10B981",
    icon: <Lightbulb className="w-4 h-4" />,
  },
  build_relationships_belonging: {
    key: "buildRelationshipsBelonging",
    label: "Relationships",
    fullLabel: "Build Relationships & Belonging",
    color: "#F43F5E",
    icon: <Heart className="w-4 h-4" />,
  },
  effective_instructional_strategies: {
    key: "effectiveInstructionalStrategies",
    label: "Instructional Strategies",
    fullLabel: "Effective Instructional Strategies",
    color: "#7C3AED",
    icon: <Zap className="w-4 h-4" />,
  },
  check_for_understanding_feedback: {
    key: "checkForUnderstandingFeedback",
    label: "Check for Understanding",
    fullLabel: "Check for Understanding & Feedback",
    color: "#0891B2",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  pacing_engagement: {
    key: "pacingEngagement",
    label: "Pacing & Engagement",
    fullLabel: "Pacing & Engagement",
    color: "#D97706",
    icon: <Clock className="w-4 h-4" />,
  },
  instructional_adaptability: {
    key: "instructionalAdaptability",
    label: "Instructional Adaptability",
    fullLabel: "Instructional Adaptability",
    color: "#06B6D4",
    icon: <Zap className="w-4 h-4" />,
  },
};

/** Fallback palette for skill categories not listed in SCORE_CATEGORY_PRESETS */
const PRESET_PALETTE = [
  "#6366F1",
  "#DB2777",
  "#059669",
  "#CA8A04",
  "#7C2D12",
  "#0E7490",
  "#4F46E5",
  "#BE185D",
  "#B45309",
  "#0D9488",
  "#A21CAF",
  "#4338CA",
  "#B91C1C",
];

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function skillCategoryToCamelKey(skillCategory) {
  if (!skillCategory || typeof skillCategory !== "string") {
    return null;
  }
  return skillCategory.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function titleCaseFromSnake(skillCategory) {
  return skillCategory
    .split(/_/g)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Config for a skill category (preset or derived for unknown institutions).
 * @param {string} skillCategory
 */
export function getMetricConfigForCategory(skillCategory) {
  if (!skillCategory) {
    return null;
  }
  const preset = SCORE_CATEGORY_PRESETS[skillCategory];
  if (preset) {
    return { ...preset, skillCategory };
  }
  const key = skillCategoryToCamelKey(skillCategory);
  if (!key) {
    return null;
  }
  const label = titleCaseFromSnake(skillCategory);
  const color =
    PRESET_PALETTE[hashString(skillCategory) % PRESET_PALETTE.length];
  return {
    key,
    label,
    fullLabel: label,
    color,
    icon: <BookOpen className="w-4 h-4" />,
    skillCategory,
  };
}

export function normalizeScoreValue(score) {
  if (score === undefined || score === null || Number.isNaN(Number(score))) {
    return null;
  }
  const n = Number(score);
  if (n <= 1) {
    return Math.round(n * 100);
  }
  return Math.round(n * 10);
}

export function collectMetricConfigsFromSessions(sessions) {
  const seen = new Map();
  if (!sessions || !sessions.length) {
    return [];
  }
  sessions.forEach((session) => {
    const soft =
      session.soft_skills_evaluation_2?.scoring || session.scoring || [];
    soft.forEach((item) => {
      if (!item || !item.skillCategory) {
        return;
      }
      const cfg = getMetricConfigForCategory(item.skillCategory);
      if (cfg && !seen.has(cfg.key)) {
        seen.set(cfg.key, cfg);
      }
    });
  });
  return Array.from(seen.values()).sort((a, b) =>
    a.fullLabel.localeCompare(b.fullLabel)
  );
}
